import type { CreatorMetrics } from "@unbox/api";
import { type ReactNode, useMemo } from "react";
import type { UnboxTableColumn, UnboxTableRow } from "@/components/application/table/unbox-table.types";
import { useCurrency } from "@/hooks/use-currency";
import { formatDate } from "@/utils/date-conversion";

type PeriodMetricKey =
  | "orders"
  | "ordersPerWeek"
  | "revenue"
  | "avgGmv"
  | "gpm"
  | "aov"
  | "ctr"
  | "ctor"
  | "postsPerWeek";

// Best Matches sends no period metrics for a candidate that isn't a brand creator yet.
type CreatorMetricFields = { [Key in PeriodMetricKey]: CreatorMetrics[Key] | null } & Pick<
  CreatorMetrics,
  | "postingFrequency"
  | "avgLikesPerVideo"
  | "topCategories"
  | "avgCommissionRate"
  | "sellerRating"
  | "lastSale"
  | "lastActivity"
  | "lastContacted"
  | "campaigns"
  | "avgVideosPerMonth"
  | "topProduct"
  | "commonViolations"
  | "videosWithRisks"
> & { unitSales: number | null };

const decimalFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
const compactFormatter = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });
const percentFormatter = new Intl.NumberFormat("en-US", { style: "percent", maximumFractionDigits: 1 });

const text = (value: string | number | null) => (
  <span className="font-medium whitespace-nowrap">{value === null || value === "" ? "-" : value}</span>
);

const formatted = (value: number | null, formatter: Intl.NumberFormat) =>
  text(value === null ? null : formatter.format(value));

const list = (values: string[]) =>
  values.length === 0 ? (
    "-"
  ) : (
    <span className="block max-w-[220px] truncate font-medium" title={values.join(", ")}>
      {values.join(", ")}
    </span>
  );

/**
 * Columns shared by the Your Creators table and Best Matches, so both render the same layout.
 * Blocked creators show no metrics, matching the existing columns.
 */
export const useCreatorMetricColumns = <
  Row extends UnboxTableRow & CreatorMetricFields & { isBlocked?: boolean },
>(): UnboxTableColumn<Row>[] => {
  const currency = useCurrency();

  return useMemo(() => {
    const money = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      notation: "compact",
      compactDisplay: "short",
    });
    const column = (
      id: string,
      label: string,
      render: (row: Row) => ReactNode,
      options: { sortable?: boolean; minWidth?: string } = {},
    ): UnboxTableColumn<Row> => ({
      id,
      label,
      sortable: options.sortable,
      minWidth: options.minWidth ?? "130px",
      render: (_value, row) => (row.isBlocked ? null : render(row)),
    });

    return [
      column("avgGmv", "Avg GMV", (row) => formatted(row.avgGmv, money), { sortable: true }),
      column("orders", "Orders", (row) => formatted(row.orders, decimalFormatter), { sortable: true }),
      column("ordersPerWeek", "Orders per week (avg)", (row) => formatted(row.ordersPerWeek, decimalFormatter), {
        sortable: true,
        minWidth: "170px",
      }),
      column("revenue", "Revenue", (row) => formatted(row.revenue, money), { sortable: true }),
      column("gpm", "GPM", (row) => formatted(row.gpm, money), { sortable: true }),
      column("aov", "AOV", (row) => formatted(row.aov, money), { sortable: true }),
      column(
        "unitSales",
        "Units Sold",
        (row) => text(row.unitSales === null ? null : decimalFormatter.format(row.unitSales)),
        { sortable: true },
      ),
      // Date-only values already read YYYY-MM-DD; parsing them as a Date would shift them a day west of UTC.
      column("lastSale", "Last Sale", (row) => text(row.lastSale)),
      column("ctr", "CTR", (row) => formatted(row.ctr, percentFormatter), { sortable: true }),
      column("ctor", "CTOR", (row) => formatted(row.ctor, percentFormatter), { sortable: true }),
      column("postsPerWeek", "Posts per week (avg)", (row) => formatted(row.postsPerWeek, decimalFormatter), {
        sortable: true,
        minWidth: "170px",
      }),
      column(
        "avgVideosPerMonth",
        "Avg Videos per Month",
        (row) => text(row.avgVideosPerMonth === null ? null : decimalFormatter.format(row.avgVideosPerMonth)),
        { minWidth: "170px" },
      ),
      column("postingFrequency", "Posting Frequency", (row) =>
        text(row.postingFrequency === null ? null : percentFormatter.format(row.postingFrequency)),
      ),
      column("avgLikesPerVideo", "Avg Likes per Video", (row) =>
        text(row.avgLikesPerVideo === null ? null : compactFormatter.format(row.avgLikesPerVideo)),
      ),
      column("topCategories", "Top Selling Categories", (row) => list(row.topCategories), { minWidth: "180px" }),
      column("topProduct", "Top Selling Product", (row) => list(row.topProduct ? [row.topProduct] : []), {
        minWidth: "180px",
      }),
      column("avgCommissionRate", "Avg Commission Rate", (row) =>
        text(row.avgCommissionRate === null ? null : percentFormatter.format(row.avgCommissionRate)),
      ),
      column("sellerRating", "Seller Rating", (row) =>
        text(row.sellerRating === null ? null : decimalFormatter.format(row.sellerRating)),
      ),
      column("campaigns", "Number of Campaigns", (row) => text(row.campaigns), { minWidth: "160px" }),
      column("lastActivity", "Last Activity", (row) => text(row.lastActivity)),
      column("lastContacted", "Last Contacted", (row) =>
        text(row.lastContacted ? formatDate(row.lastContacted) : null),
      ),
      column("videosWithRisks", "Videos with Risks", (row) => text(row.videosWithRisks)),
      column("commonViolations", "Common Violations", (row) => list(row.commonViolations), { minWidth: "180px" }),
    ];
  }, [currency]);
};
